import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import Toolbar, { Item } from "devextreme-react/toolbar";
import { Button } from "devextreme-react/button";
import { LoadPanel, Popup } from "devextreme-react";

import routes from "../../../../../assets/apiRoutes.json";
import { MESSAGES } from "../../../../../common/Messages";
// The shared error-message ladder (Architecture.md rule 16). It already covers everything a local
// helper would: axios errors with a response body, PascalCase or camelCase Message, string bodies, and
// the timeout case where error.response is undefined.
import { getApiErrorMessage } from "../../../../../common/concurrency";
import { useCommittedFilterRows } from "../../../../../common/reportFilters";
import { loadLookupFromStaticDataSource } from "../../../../../common/Utils";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import CommonReport from "../../../../../reports/components/CommonReport";
import OperationResultGrid from "../../../charges/common/OperationResultGrid";
import { ParametersLayout } from "./Layout";
import { PREVIEW_COLUMNS, PREVIEW_KEY_EXPR } from "./PreviewGridColumns";
import "./setUnusedLotsInactive.css";
import {
    BUTTON,
    DEFAULT_LAYOUT_NAME,
    DO_NOTHING,
    FIELD,
    HELP_TEXT,
    LAYOUT_ID,
    LAYOUT_NAME,
    PAGE_TITLE,
    PREVIEW_GRID_KEY,
    PREVIEW_TITLE,
    RECURRING_STORAGE_OPTIONS,
    REPORT,
    REPORT_LOAD_TIMEOUT_MS,
    REQUEST_TIMEOUT_MS,
    toDateOnly,
    toFlag,
} from "./constants";

const BUSY = Object.freeze({ IDLE: "idle", PREVIEWING: "previewing", UPDATING: "updating" });

/**
 * Puts a class on the Preview popup's WRAPPER so ../../../charges/common/operationScreen.css can reach
 * `.dx-popup-content` — the element DevExtreme sizes, and the one that has to become a flex column for
 * the grid to fill it. There is no other hook: the popup renders into an overlay outside this
 * component's DOM subtree.
 *
 * Module-level so the object identity is stable; an inline literal would be a new object every render
 * and make the Popup re-apply it.
 */
const PREVIEW_POPUP_ATTR = { class: "op-preview-popup" };

/**
 * The empty row set, as ONE module-level array.
 *
 * A fresh `[]` on every clear would be a new prop identity each time, which defeats the memo on
 * OperationResultGrid — and a re-render there rebuilds the grid's toolbar template, remounting
 * GridLayoutManager and firing another GetGridLayouts. Not frozen: DevExtreme is free to read this as a
 * dataSource and Object.freeze would turn any internal in-place work into a throw.
 */
const NO_PREVIEW_ROWS = [];

/**
 * Did the user actually enter any Record Range criteria?
 *
 * Tested against the COMMITTED rows, i.e. what `readFilterRows` returns, so a value typed without
 * tabbing out is not mistaken for an empty filter — on this screen that mistake would ask the user to
 * confirm updating every eligible record. Mirrors the same helper on the sibling operation screens.
 *
 * Only `from` counts: a row carrying nothing but a sort produces no predicate, so the server would see
 * an empty WHERE and update company-wide.
 */
const hasFilterCriteria = (rows) =>
    (rows || []).some((row) => row && row.from !== null && row.from !== undefined && row.from !== "");

/**
 * Is there anything for the run to do? True when valid.
 *
 * Mirrors SetUnusedLotsInactiveService.ValidateRequest, which is in turn btnStartClick's own guard: at
 * least one of the three checkboxes, or at least one storage flag actually chosen. Note what is NOT
 * here — there is no cascade between the checkboxes, unlike the purge screen's level selector. The three
 * are independent and every combination of them is legal except all-clear-with-no-flag.
 *
 * Checked here so an unusable selection costs no round trip; the server keeps the identical check,
 * because this one IS validation and a direct API call must not get past it.
 */
const isValidSelection = (fields) =>
    Boolean(
        fields[FIELD.UpdateLots]
        || fields[FIELD.UpdateSublots]
        || fields[FIELD.UpdateUnits]
        || toFlag(fields[FIELD.LotRecurringStorage])
        || toFlag(fields[FIELD.SublotRecurringStorage])
    );

/**
 * A function, not a module-level constant: Reset must hand back a fresh object, and a shared one would
 * be mutated across resets.
 *
 * Defaults are FormAbLoadProgram's: the date cleared (it sets the control to 0, which renders blank) and
 * both storage combos on "Do Nothing" (ItemIndex := 0). The three checkboxes carry State = cbUnchecked
 * in the DFM, so all three start clear — which means a freshly opened screen can do nothing until the
 * user chooses something, exactly as in AI8.
 */
const initialFormFields = () => ({
    [FIELD.LastUpdateDate]: null,
    [FIELD.UpdateLots]: false,
    [FIELD.UpdateSublots]: false,
    [FIELD.UpdateUnits]: false,
    [FIELD.LotRecurringStorage]: DO_NOTHING,
    [FIELD.SublotRecurringStorage]: DO_NOTHING,
});

/**
 * WM "Set Unused Lots To Inactive Status" (legacy WMD0068).
 *
 * Start sets STATUS to Inactive on the IMLOT, IMSUBLOT and IMUNIT records that hold no inventory and
 * were last updated on or before the date entered, and/or sets the lot and sublot Recurring Storage flag.
 * One run can do all of that at once: the three checkboxes are independent, not a level selector.
 *
 * Preview is a different question, and the screen says so rather than quietly redefining it: the Delphi
 * Preview button runs the where-builder's own dataset, which is IM Item Master, so it lists the ITEMS the
 * Record Range selects — not the lots the update would change. Reproduced because users know the screen.
 *
 * This is an OPERATION screen: no CustomStore, no <Editing>, no per-row read-modify-write, so
 * Architecture.md rules 4-6, 12 and 14-16 (including optimistic concurrency) do not apply.
 *
 * The legacy Help and Exit buttons are dropped: Help opened a Delphi context topic and Exit closed the
 * MDI child, neither of which has a web equivalent.
 */
const SetUnusedLotsInactive = (props) => {
    const accessToken = props.oidc?.user?.access_token;
    const currentUser = props.oidc?.user;
    const dynamicFormSetting = props.oidc?.user?.profile?.DYNAMICFORMSETTING;

    const formRefs = useRef({});
    const reportRef = useRef(null);
    const previewGrid = useRef(null);

    const [formFields, setFormFields] = useState(initialFormFields);
    const [filterWhereInfoList, setFilterWhereInfoList] = useState([]);
    const [busy, setBusy] = useState(BUSY.IDLE);

    /**
     * One object rather than three useStates. React 17 does not batch setState across an await, so
     * three separate updates after the fetch meant three extra renders of the page — and each one
     * rebuilt the grid's toolbar template, remounting GridLayoutManager and firing another
     * GetGridLayouts.
     */
    const [preview, setPreview] = useState({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
    const [previewLayout, setPreviewLayout] = useState(DEFAULT_LAYOUT_NAME);

    /**
     * Whether the Preview popup exists in the tree at all. False until the user first presses Preview.
     *
     * The popup contains OperationResultGrid, which on mount runs GetGridLayouts and its StateStoring
     * customLoad. None of that is work the user asked for while they are filling the form in — so the
     * grid simply is not there to do it. It mounts when Preview is pressed, which is the moment those
     * calls are legitimate.
     *
     * Set one render BEFORE `visible` flips, deliberately: DevExtreme captures a Popup's content when the
     * popup is created, and this codebase has been bitten by a `{state && …}` guard inside a Popup
     * capturing an empty template that never filled (see the sibling screens' result dialogs). Gating the
     * whole Popup element, and mounting it in an earlier commit than the one that shows it, avoids that
     * shape entirely.
     */
    const [previewMounted, setPreviewMounted] = useState(false);

    /**
     * True while GetReport is in flight — on first mount and on Reset. Starts true because the first
     * render already triggers the fetch.
     *
     * Unlike the purge screen there is no level to change, so this is the whole of the catalogue's
     * lifecycle: <CommonReport> mounts once and is reloaded only by Reset.
     */
    const [reportLoading, setReportLoading] = useState(true);
    const reportLoadTimer = useRef(null);

    const isBusy = busy !== BUSY.IDLE || reportLoading;

    /**
     * A run or a preview outranks the catalogue fetch: both can only start once the fields are loaded,
     * so if one is running that is what the user is waiting on.
     */
    const loadPanelMessage = busy === BUSY.UPDATING
        ? MESSAGES.SET_INACTIVE_UPDATING
        : busy === BUSY.PREVIEWING
            ? MESSAGES.SET_INACTIVE_PREVIEWING
            : MESSAGES.SET_INACTIVE_LOADING_FIELDS;

    const postOptions = useCallback(
        () => ({
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
            timeout: REQUEST_TIMEOUT_MS,
        }),
        [accessToken]
    );

    const clearReportLoadTimer = useCallback(() => {
        if (reportLoadTimer.current) {
            clearTimeout(reportLoadTimer.current);
            reportLoadTimer.current = null;
        }
    }, []);

    /**
     * Raises the load panel for a GetReport that is about to start, and arms a deadline.
     *
     * The deadline is not belt-and-braces, it is required. CommonReport.loadReport's only error handling
     * is `.catch((err) => console.log(...))` (CommonReport.js:555) — it does NOT call
     * onReportStateChange on failure. So a GetReport that 500s or times out would otherwise leave this
     * panel up forever, with shading on, locking the whole page behind a modal overlay. Better to drop
     * the panel and say so.
     *
     * The right fix is for CommonReport to surface the failure; that is shared code serving every report
     * screen, so it is a separate change rather than something to smuggle in here.
     */
    const beginReportLoad = useCallback(() => {
        clearReportLoadTimer();
        setReportLoading(true);

        reportLoadTimer.current = setTimeout(() => {
            reportLoadTimer.current = null;
            setReportLoading(false);
            notify({ message: MESSAGES.SET_INACTIVE_FIELDS_LOAD_FAILED, width: 450 }, "error", 5000);
        }, REPORT_LOAD_TIMEOUT_MS);
    }, [clearReportLoadTimer]);

    /**
     * CommonReport fires this once per mount, from loadReport's .then. It stays correct because the
     * editable filter grid is bound to an ArrayStore wrapping this same array instance, which cell edits
     * mutate in place — so the reference captured here stays live. Do not clone it per render.
     *
     * Also the one signal that GetReport finished, so it is what lowers the load panel.
     */
    const handleReportStateChange = useCallback((reportState) => {
        clearReportLoadTimer();
        setReportLoading(false);
        setFilterWhereInfoList(reportState?.filterWhereInfoList ?? []);
    }, [clearReportLoadTimer]);

    // Arms the deadline for the fetch the FIRST render already kicked off (reportLoading starts true, so
    // the panel is already showing; this only adds the timeout), and disarms it on unmount so a pending
    // timer cannot fire setState against a gone component.
    useEffect(() => {
        beginReportLoad();

        return clearReportLoadTimer;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Preview and Start send the identical body, so neither can end up acting on a different row set
     * than the other showed — even though on this screen the two queries answer different questions.
     *
     * The storage combos go through toFlag, which maps the "Do Nothing" sentinel to null; the date goes
     * through toDateOnly, which keeps a calendar date from drifting a day across time zones. See both in
     * constants.js.
     */
    const buildRequest = useCallback(
        (rows) => ({
            [FIELD.LastUpdateDate]: toDateOnly(formFields[FIELD.LastUpdateDate]),
            [FIELD.UpdateLots]: formFields[FIELD.UpdateLots],
            [FIELD.UpdateSublots]: formFields[FIELD.UpdateSublots],
            [FIELD.UpdateUnits]: formFields[FIELD.UpdateUnits],
            [FIELD.LotRecurringStorage]: toFlag(formFields[FIELD.LotRecurringStorage]),
            [FIELD.SublotRecurringStorage]: toFlag(formFields[FIELD.SublotRecurringStorage]),
            FilterWhereInfoList: rows || [],
        }),
        [formFields]
    );

    /**
     * Commits any in-progress filter cell before reading the rows — see common/reportFilters.js for why
     * the onReportStateChange array is the wrong source at submit time.
     *
     * Load-bearing on THIS screen specifically: a filter the user typed but has not tabbed out of would
     * otherwise read as empty, which makes Start ask "no criteria — update EVERYTHING?" and, if answered
     * Yes, reach far further than was asked for.
     */
    const readFilterRows = useCommittedFilterRows(reportRef, filterWhereInfoList);

    // Stable identity: an inline arrow here would change on every render and, via the Popup, keep the
    // grid subtree re-rendering.
    const hidePreview = useCallback(() => {
        setPreview((previous) => ({ ...previous, visible: false }));
    }, []);

    /**
     * Required: FormField calls onFormLoad() in fetchData's `finally` with no guard and no default
     * (FormField.js:256), so omitting it throws "onFormLoad is not a function" and the parameters panel
     * never renders.
     *
     * Mandatory for the two selects as well, not merely an optimisation: Fields.js renders a `select`
     * with NO dataSource at all (Architecture.md rule 3 — load the options via formRefs in onFormLoad).
     * FormField calls this BEFORE React has committed the child Fields, so formRefs is still empty at
     * that instant; hence the same 100ms shape the siblings use.
     *
     * No cascade to apply afterwards, unlike the purge screen: nothing on this form enables or disables
     * anything else.
     */
    const handleFormLoad = useCallback(() => {
        setTimeout(() => {
            loadLookupFromStaticDataSource(
                FIELD.LotRecurringStorage, RECURRING_STORAGE_OPTIONS, formFields, formRefs);
            loadLookupFromStaticDataSource(
                FIELD.SublotRecurringStorage, RECURRING_STORAGE_OPTIONS, formFields, formRefs);
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * The selection check both buttons run before they post, and the date check Start adds.
     *
     * Shows the same red toast the server would have, without spending a round trip to be told what the
     * screen already knows. The selection wording is the Delphi's own ("You Have NOT Made Any
     * Selections"), shown by btnPreviewClick and btnStartClick alike.
     *
     * The date is required for Start only — it constrains the lot/sublot/unit rows the UPDATEs touch and
     * has no part in the Preview query, and the desktop Preview does not ask for it either. The server
     * draws the same line.
     */
    const canRun = useCallback((requireDate) => {
        if (!isValidSelection(formFields)) {
            notify({ message: MESSAGES.SET_INACTIVE_NO_SELECTIONS, width: 450 }, "error", 5000);
            return false;
        }

        if (requireDate && !formFields[FIELD.LastUpdateDate]) {
            notify({ message: MESSAGES.SET_INACTIVE_DATE_REQUIRED, width: 450 }, "error", 5000);
            return false;
        }

        return true;
    }, [formFields]);

    const handlePreview = useCallback(async () => {
        if (!canRun(false)) {
            return;
        }

        // Brings the popup (and with it the grid) into the tree now, a commit before `visible` flips
        // below — see previewMounted. Everything the grid does on mount is legitimate from here on: the
        // user has asked to see it.
        setPreviewMounted(true);

        // Commits any in-progress filter cell first, so Preview matches what the user typed.
        const filterRows = await readFilterRows();

        setBusy(BUSY.PREVIEWING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMSetUnusedLotsInactive.GetRecords}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;

            // A row array is the ONLY shape treated as success; anything else is an error, whatever the
            // envelope claims. Two failure modes reach here as HTTP 200: a refusal decided BEFORE the
            // query runs (an unusable selection, a filter naming a column this screen cannot reach),
            // which arrives as Success:false with no Resultdata; and one that fails DURING the query,
            // which a Success-only check misses. Requiring the array closes both.
            if (payload?.Success === false || !Array.isArray(payload?.Resultdata)) {
                setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
                notify({ message: payload?.Message || MESSAGES.ERROR, width: 450 }, "error", 5000);
                return;
            }

            // An empty array IS legitimate — the grid says so via noDataText.
            setPreview({ rows: payload.Resultdata, visible: true, hasRun: true });
        } catch (error) {
            setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [canRun, readFilterRows, buildRequest, postOptions]);

    /**
     * Start: ask, then run. One POST.
     *
     * Both questions are the web's, not the legacy's — AI8 ran straight off the button. Both are
     * acknowledgements and neither needs the database: the first is unconditional, and the second only
     * needs to know whether the user entered any criteria, which the committed filter rows already tell
     * us. Stopping at the first No matches how every sibling screen asks.
     *
     * Titled MESSAGES.CONFIRM, as the Delphi dialogs on this family of screens are.
     */
    const handleStart = useCallback(async () => {
        // Before the confirmations: no point asking someone to acknowledge a run that could not proceed.
        if (!canRun(true)) {
            return;
        }

        // Read BEFORE the confirmations, not after: the empty-filter question has to see the COMMITTED
        // rows, or a value the user typed without tabbing out looks like no filter at all and they get
        // asked to update everything.
        const filterRows = await readFilterRows();

        if ((await confirm(MESSAGES.SET_INACTIVE_CONFIRM, MESSAGES.CONFIRM)) !== true) {
            return;
        }

        if (!hasFilterCriteria(filterRows)
            && (await confirm(MESSAGES.SET_INACTIVE_NO_FILTER_CRITERIA_CONFIRM, MESSAGES.CONFIRM)) !== true) {
            return;
        }

        setBusy(BUSY.UPDATING);
        try {
            const response = await axios.post(
                `${window._env_.REACT_APP_APIS}/${routes.WMSetUnusedLotsInactive.Start}`,
                JSON.stringify(buildRequest(filterRows)),
                postOptions()
            );

            const payload = response?.data;
            const data = payload?.Resultdata;

            if (data == null || (data.TotalUpdated ?? 0) <= 0) {
                notify(
                    { message: payload?.Message || MESSAGES.NO_RECORD_FOUND_RANGE, width: 450 },
                    "error",
                    5000
                );
                return;
            }

            // Green, and the preview is dropped: the items it listed still exist, but their lots' status
            // has moved under it, and a grid that no longer reflects the database reads as stale.
            notify({ message: payload.Message, width: 450 }, "success", 5000);
            setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
        } catch (error) {
            notify({ message: getApiErrorMessage(error, MESSAGES.ERROR), width: 450 }, "error", 5000);
        } finally {
            setBusy(BUSY.IDLE);
        }
    }, [canRun, readFilterRows, buildRequest, postOptions]);

    /**
     * Reset: the form back to its load-time defaults and the Record Range emptied
     * (imSetStatustoInactive.Reset, uWMD0068.pas btnResetFilterClick — which resets only the filter;
     * FormAbLoadProgram is what sets the rest, and this does both).
     *
     * loadReport is called unconditionally, unlike the purge screen's, which has to skip it when the
     * report name changes. Here the name never changes, so <CommonReport> is never remounted and the
     * explicit reload is the only thing that rebuilds the filter store and re-fires
     * onReportStateChange with a fresh array.
     */
    const handleReset = useCallback(() => {
        setFormFields(initialFormFields());
        setPreview({ rows: NO_PREVIEW_ROWS, visible: false, hasRun: false });
        // Unmounts the grid, so the next Preview mounts it fresh rather than showing rows from a filter
        // that no longer exists. previewLayout is deliberately NOT reset: OperationResultGrid memoises
        // its StateStoring customLoad/customSave on currentLayout, so changing it would re-run customLoad
        // and fire another GetGridLayout for a grid that is not even mounted.
        setPreviewMounted(false);
        beginReportLoad();

        if (reportRef.current && typeof reportRef.current.loadReport === "function") {
            reportRef.current.loadReport();
        }
    }, [beginReportLoad]);

    return (
        <>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                visible={isBusy}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
                message={loadPanelMessage}
            />

            {/*
                Delphi's grpMain is a centred bold title band above the where-builder. In the web app the
                page title lives in the toolbar, so the band collapses into this header rather than being
                reproduced as a separate strip.
            */}
            <div className="row">
                <div className="col-md-12 main-page">
                    <Toolbar className="header">
                        <Item location="before">
                            <div className="title">
                                <h6 style={{ color: window._env_.Color2, marginLeft: "12px" }}>{PAGE_TITLE}</h6>
                            </div>
                        </Item>

                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Start} type="default" onClick={handleStart} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Preview} onClick={handlePreview} disabled={isBusy} />
                        </Item>
                        <Item location="after">
                            <Button className="accordianButton" text={BUTTON.Reset} onClick={handleReset} disabled={isBusy} />
                        </Item>
                    </Toolbar>
                </div>
            </div>

            <div className="suli-page">
                {/* The Delphi criteria memo (edtMemo), which sits at the top of the form under the title. */}
                <div className="op-help suli-help">{HELP_TEXT}</div>

                {/*
                    Record Range directly under the criteria memo, exactly as the Delphi form lays it out —
                    imSetStatusToInactive fills the middle of the form and pnlBottom sits under it.

                    `key` is not needed here and is deliberately absent: the report name never changes on
                    this screen, so there is nothing to remount for. (Contrast the purge screen, where the
                    key IS mandatory because CommonReport's own reload-on-name-change guard compares
                    prevProps.Name against this.props.Name while loadReport reads this.props.name, so it
                    never fires — CommonReport.js:112 vs :448.)
                */}
                <div className="op-filter">
                    <CommonReport
                        name={REPORT.name}
                        fullName={REPORT.fullName}
                        onReportStateChange={handleReportStateChange}
                        innerRef={reportRef}
                    />
                </div>

                {/*
                    Delphi's pnlBottom, below the Record Range: grpBottomLeft (the date), grpBottomRight
                    (the three checkboxes) and grpBottomRightStorage (the two combos), side by side in that
                    order. The three form groups ARE that row — FormField gives each group col-md-4 — so
                    there is no layout here beyond the groups themselves.
                */}
                <div className="op-parameters suli-parameters">
                    <FormField
                        currrentUser={currentUser}
                        layoutId={LAYOUT_ID}
                        layoutName={LAYOUT_NAME}
                        defaultFormSchema={ParametersLayout}
                        formFields={formFields}
                        setformFields={setFormFields}
                        DYNAMICFORMSETTING={dynamicFormSetting}
                        formRefs={formRefs}
                        onFormLoad={handleFormLoad}
                    />
                </div>
            </div>

            {/*
                Gated on previewMounted, not merely hidden with visible=false. A rendered-but-hidden popup
                still mounts OperationResultGrid, and that mount is what fires GetGridLayouts and the
                StateStoring customLoad. Filling the form in should cost one request (GetReport), so until
                Preview is pressed there is no grid in the tree to make any of those calls.
            */}
            {previewMounted && (
                <Popup
                    visible={preview.visible}
                    onHiding={hidePreview}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    title={PREVIEW_TITLE}
                    width="94vw"
                    height="76vh"
                    wrapperAttr={PREVIEW_POPUP_ATTR}
                >
                    {/*
                        The grid FILLS the popup rather than being given a computed height — see
                        .op-preview-popup in the shared operationScreen.css.

                        No ScrollView wrapper: the grid owns both scroll axes, and an outer scroller would
                        give it an unbounded width to lay out against, killing horizontal scrolling.
                    */}
                    <div className="op-preview-body">
                        <div className="op-preview-grid">
                            {/*
                                No `key` and no per-level gridKey, unlike the purge screen: there is one
                                column set here, because there is one dataset behind the Preview. A key
                                would only remount the grid and re-fire GetGridLayouts for nothing.
                            */}
                            <OperationResultGrid
                                rows={preview.rows}
                                columns={PREVIEW_COLUMNS}
                                keyExpr={PREVIEW_KEY_EXPR}
                                noDataText={preview.hasRun ? MESSAGES.SET_INACTIVE_NO_RECORDS_FOUND : ""}
                                height="100%"
                                gridRef={previewGrid}
                                gridKey={PREVIEW_GRID_KEY}
                                currentLayout={previewLayout}
                                setCurrentLayout={setPreviewLayout}
                                accessToken={accessToken}
                            />
                        </div>

                        {/*
                            The row count, as TLookupForm shows it under its grid (SearchFm.pas lbInfo).
                            Counted from preview.rows, i.e. the whole result set — NOT the grid's current
                            page or its filter-row state. Hidden at zero rows: noDataText already covers
                            that.
                        */}
                        {preview.rows.length > 0 && (
                            <div className="op-preview-footer">
                                {MESSAGES.PREVIEW_RECORDS_MATCHING_FORMAT.replace("{0}", preview.rows.length)}
                            </div>
                        )}
                    </div>
                </Popup>
            )}
        </>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(SetUnusedLotsInactive);
